import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, CalendarDays, RotateCw, Trash2, Save, CheckCircle2, AlertTriangle } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { updatePreferences, syncCalendar, disconnectCalendar } from "@/lib/api";

export const CalendarSyncDialog = ({ preferences, templates = [], onClose, onSaved, onSynced }) => {
  const { t } = useI18n();
  const configured = !!preferences?.calendar_configured;
  const [url, setUrl] = useState("");
  const [enabled, setEnabled] = useState(!!preferences?.calendar_sync_enabled);
  const [templateId, setTemplateId] = useState(
    preferences?.calendar_template_id || ""
  );
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    setEnabled(!!preferences?.calendar_sync_enabled);
    setTemplateId(preferences?.calendar_template_id || "");
  }, [preferences]);

  const handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = {
      calendar_sync_enabled: enabled,
    };
    if (url.trim()) {
      payload.calendar_url = url.trim();
    } else if (!configured && enabled) {
      toast.error("ADD A URL FIRST");
      return;
    }
    if (templateId) payload.calendar_template_id = templateId;

    try {
      setSaving(true);
      const next = await updatePreferences(payload);
      toast.success(t("cal.saved"));
      onSaved?.(next);
      setUrl("");
      // Auto-sync immediately if enabled and configured
      if (next.calendar_sync_enabled && next.calendar_configured) {
        try {
          setSyncing(true);
          const r = await syncCalendar();
          toast.success(t("cal.synced").replace("{n}", r.count ?? 0));
          onSynced?.();
        } catch (err) {
          toast.error(t("cal.failed"));
        } finally {
          setSyncing(false);
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t("cal.failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleSyncNow = async () => {
    try {
      setSyncing(true);
      const r = await syncCalendar();
      toast.success(t("cal.synced").replace("{n}", r.count ?? 0));
      onSynced?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t("cal.failed"));
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectCalendar();
      toast.success(t("cal.disconnected"));
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(t("cal.failed"));
    }
  };

  const lastSync = preferences?.calendar_last_synced_at
    ? new Date(preferences.calendar_last_synced_at).toLocaleString()
    : null;
  const lastStatus = preferences?.calendar_last_sync_status || null;

  return (
    <div
      data-testid="calendar-sync-dialog"
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/60 overflow-y-auto"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <form
        onSubmit={handleSave}
        className="relative bg-white brutal-border brutal-shadow w-full max-w-lg p-6 font-mono my-6"
      >
        <button
          type="button"
          data-testid="calendar-sync-close"
          onClick={onClose}
          className="absolute top-3 right-3 bg-white brutal-border w-9 h-9 flex items-center justify-center brutal-shadow-sm brutal-interactive"
        >
          <X className="w-5 h-5" strokeWidth={3} />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="bg-[#00CCFF] brutal-border w-10 h-10 flex items-center justify-center">
            <CalendarDays className="w-5 h-5" strokeWidth={3} />
          </div>
          <div>
            <h3 className="font-display text-xl uppercase leading-none">
              {t("cal.title")}
            </h3>
            <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
              {t("cal.badge")}
            </div>
          </div>
        </div>

        {/* Connection status chip */}
        <div
          data-testid="calendar-status-chip"
          className={`inline-flex items-center gap-1 brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest mb-4 ${
            configured ? "bg-[#E5FF00]" : "bg-white text-neutral-500"
          }`}
        >
          {configured ? (
            <>
              <CheckCircle2 className="w-3 h-3" strokeWidth={3} /> {t("cal.configured")}
            </>
          ) : (
            <>
              <AlertTriangle className="w-3 h-3" strokeWidth={3} /> {t("cal.notConfigured")}
            </>
          )}
        </div>

        {/* URL input */}
        <label className="block font-mono text-[10px] uppercase tracking-widest mb-1 font-bold text-neutral-600">
          {t("cal.urlLabel")}
        </label>
        <input
          data-testid="calendar-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={configured ? "•••••••••••••••••••••••••• (saved)" : t("cal.urlPlaceholder")}
          className="w-full bg-white brutal-border p-3 text-sm font-mono focus:outline-none focus:bg-[#FFF9B0] transition-colors"
        />
        <p className="font-mono text-[10px] text-neutral-500 mt-1 mb-4 leading-relaxed">
          {t("cal.urlHint")}
        </p>

        {/* Template picker */}
        <label className="block font-mono text-[10px] uppercase tracking-widest mb-1 font-bold text-neutral-600">
          {t("cal.templateLabel")}
        </label>
        <select
          data-testid="calendar-template"
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          className="w-full bg-white brutal-border p-3 text-sm font-mono focus:outline-none focus:bg-[#FFF9B0] transition-colors mb-4"
        >
          <option value="">{t("cal.templateNone")}</option>
          {templates.map((tpl) => (
            <option key={tpl.id} value={tpl.id}>
              {tpl.name}
            </option>
          ))}
        </select>

        {/* Enable toggle */}
        <button
          type="button"
          data-testid="calendar-toggle"
          onClick={() => setEnabled((v) => !v)}
          className={`w-full flex items-center justify-between gap-3 brutal-border p-3 brutal-shadow-sm brutal-interactive mb-4 ${
            enabled ? "bg-[#E5FF00]" : "bg-white"
          }`}
        >
          <span className="font-display uppercase text-sm">
            {enabled ? t("cal.toggleOn") : t("cal.toggleOff")}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest">
            {enabled ? "[ON]" : "[OFF]"}
          </span>
        </button>

        <p className="font-mono text-[10px] text-neutral-500 italic mb-4">
          {t("cal.titleTrim")}
        </p>

        {/* Last sync info */}
        {lastSync && (
          <div className="brutal-border p-3 mb-4 bg-[#F2F2F0] font-mono text-[11px] space-y-1">
            <div className="flex justify-between gap-2">
              <span className="uppercase tracking-widest text-neutral-500">
                {t("cal.lastSync")}
              </span>
              <span data-testid="calendar-last-sync">{lastSync}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="uppercase tracking-widest text-neutral-500">
                {t("cal.statusLabel")}
              </span>
              <span
                className={
                  lastStatus === "ok" ? "text-green-700" : "text-red-700"
                }
                data-testid="calendar-last-status"
              >
                {lastStatus === "ok"
                  ? `${t("cal.statusOk")} · ${t("cal.lastCount").replace("{n}", preferences?.calendar_last_sync_count ?? 0)}`
                  : (lastStatus || "")}
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="submit"
            data-testid="calendar-save"
            disabled={saving || syncing}
            className="flex-1 min-w-[160px] bg-black text-white brutal-border px-4 py-3 font-display uppercase text-base brutal-shadow brutal-interactive disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Save className="w-4 h-4" strokeWidth={3} />
            {saving ? t("cal.saving") : syncing ? t("cal.syncing") : t("cal.save")}
          </button>
          {configured && (
            <button
              type="button"
              data-testid="calendar-sync-now"
              onClick={handleSyncNow}
              disabled={syncing || saving}
              title={t("cal.syncNow")}
              className="bg-[#E5FF00] brutal-border w-11 h-11 flex items-center justify-center brutal-shadow-sm brutal-interactive disabled:opacity-50"
            >
              <RotateCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} strokeWidth={3} />
            </button>
          )}
          {configured && (
            <button
              type="button"
              data-testid="calendar-disconnect"
              onClick={handleDisconnect}
              title={t("cal.disconnect")}
              className="bg-white brutal-border w-11 h-11 flex items-center justify-center brutal-shadow-sm brutal-interactive"
            >
              <Trash2 className="w-4 h-4" strokeWidth={3} />
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default CalendarSyncDialog;
